import React from 'react';
import { BankDetails } from '../../utils/types';
import { IMAGE_API_BASE_URL } from '../../config/api';
import { DOCUMENT_TYPES } from './BankFormFields';

const maskNumber = (value?: string) => {
  if (!value) return '—';
  const clean = value.trim();
  if (clean.length <= 4) return clean;
  const visible = clean.slice(-4);
  const masked = '•'.repeat(Math.min(clean.length - 4, 8));
  return `${masked} ${visible}`;
};

const getDocTypeLabel = (type?: string) => {
  if (!type) return 'Bank Document';
  const found = DOCUMENT_TYPES.find(d => d.value === type);
  if (found) return found.label;
  return type
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

interface DataFieldProps {
  label: string;
  children: React.ReactNode;
  mono?: boolean;
  colSpan?: boolean;
}

const DataField: React.FC<DataFieldProps> = ({
  label,
  children,
  mono,
  colSpan,
}) => (
  <div className={`space-y-1.5${colSpan ? ' col-span-1 sm:col-span-2' : ''}`}>
    <p className="text-xs font-medium text-muted-foreground uppercase tracking-tight">
      {label}
    </p>
    <p
      className={`text-sm font-semibold text-foreground${mono ? ' font-mono' : ''}`}
    >
      {children}
    </p>
  </div>
);

interface BankDetailsDisplayProps {
  bankData: BankDetails;
}

const BankDetailsDisplay: React.FC<BankDetailsDisplayProps> = ({
  bankData,
}) => {
  const docUrl = bankData.verificationDocument;
  const docLabel = getDocTypeLabel(bankData.verificationDocumentType);

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-5 gap-x-4">
      <DataField label="Bank Country">
        <span className="flex items-center gap-1.5">
          {bankData.bankCountry}
        </span>
      </DataField>

      <DataField label="Account Currency">
        <span className="font-mono">{bankData.currency}</span>
      </DataField>

      <DataField label="Account Holder">{bankData.accountName}</DataField>

      <DataField label="Bank Name">{bankData.bankName}</DataField>

      {bankData.accountNumber && (
        <DataField label="Account Number" mono>
          {maskNumber(bankData.accountNumber)}
        </DataField>
      )}

      {bankData.ifscCode && (
        <DataField label="IFSC Code">
          <span className="font-mono uppercase">{bankData.ifscCode}</span>
        </DataField>
      )}

      {bankData.branchName && bankData.branchName !== 'N/A' && (
        <DataField label="Branch Name">{bankData.branchName}</DataField>
      )}

      {bankData.routingNumber && (
        <DataField label="Routing Number" mono>
          {maskNumber(bankData.routingNumber)}
        </DataField>
      )}

      {bankData.sortCode && (
        <DataField label="Sort Code" mono>
          {bankData.sortCode}
        </DataField>
      )}

      {bankData.bsbNumber && (
        <DataField label="BSB Number" mono>
          {bankData.bsbNumber}
        </DataField>
      )}

      {bankData.institutionNumber && (
        <DataField label="Institution Number" mono>
          {bankData.institutionNumber}
        </DataField>
      )}

      {bankData.transitNumber && (
        <DataField label="Transit Number" mono>
          {bankData.transitNumber}
        </DataField>
      )}

      {bankData.iban && (
        <DataField label="IBAN" mono>
          {maskNumber(bankData.iban)}
        </DataField>
      )}

      {bankData.bicSwift && (
        <DataField label="BIC / SWIFT">
          <span className="font-mono uppercase">{bankData.bicSwift}</span>
        </DataField>
      )}

      {(docUrl || bankData.verificationDocumentType) && (
        <DataField label="Bank Document">
          {(() => {
            if (!docUrl) {
              return <span>{docLabel}</span>;
            }
            const cleanBase = (IMAGE_API_BASE_URL || '')
              .replace(/\/v1\/?$/, '')
              .replace(/\/+$/, '');
            const cleanPath = docUrl.startsWith('/') ? docUrl : `/${docUrl}`;
            const fullDocUrl =
              docUrl.startsWith('http://') ||
              docUrl.startsWith('https://') ||
              docUrl.startsWith('blob:')
                ? docUrl
                : `${cleanBase}${cleanPath}`;

            return (
              <a
                href={fullDocUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline font-semibold inline-flex items-center gap-1.5"
              >
                <span>{docLabel}</span>
              </a>
            );
          })()}
        </DataField>
      )}
    </div>
  );
};

export default BankDetailsDisplay;
